/**
 * AppError — discriminated union des erreurs de la couche API VisionPOS.
 *
 * Usage : toutes les fonctions de src/api/ retournent Result<T, AppError>.
 * Les consommateurs (hooks, features) font un switch(error.type) exhaustif.
 *
 * Pas d'import React ni Capacitor ici — ce fichier est pure infrastructure.
 */

import type { AuthError, PostgrestError, StorageApiError } from '@supabase/supabase-js';
import type { Result } from './result';
import { errOf } from './result';

/** Codes d'erreur fiscaux reconnus. */
export type FiscalErrorCode = '42501' | 'idempotency_violation' | 'hash_mismatch';

/**
 * Union discriminée de toutes les erreurs applicatives.
 * Chaque variant a un `type` unique pour le switch exhaustif.
 */
export type AppError =
  | { readonly type: 'network'; readonly cause: Error }
  | {
      readonly type: 'auth';
      readonly reason: 'no_jwt' | 'expired' | 'unauthorized';
    }
  | {
      readonly type: 'validation';
      readonly field: string;
      readonly message: string;
    }
  | {
      readonly type: 'not_found';
      readonly resource: string;
      readonly id: string;
    }
  | {
      readonly type: 'fiscal';
      readonly code: FiscalErrorCode;
      readonly message: string;
    }
  | {
      readonly type: 'supabase';
      readonly operation: string;
      readonly message: string;
      readonly code?: string;
    }
  | { readonly type: 'unknown'; readonly message: string };

// ---------------------------------------------------------------------------
// Helpers de construction rapide
// ---------------------------------------------------------------------------

export function networkError(cause: Error): AppError {
  return { type: 'network', cause };
}

export function authError(reason: 'no_jwt' | 'expired' | 'unauthorized'): AppError {
  return { type: 'auth', reason };
}

export function validationError(field: string, message: string): AppError {
  return { type: 'validation', field, message };
}

export function notFoundError(resource: string, id: string): AppError {
  return { type: 'not_found', resource, id };
}

export function fiscalError(code: FiscalErrorCode, message: string): AppError {
  return { type: 'fiscal', code, message };
}

export function supabaseError(operation: string, message: string, code?: string): AppError {
  return { type: 'supabase', operation, message, ...(code !== undefined ? { code } : {}) };
}

export function unknownError(message: string): AppError {
  return { type: 'unknown', message };
}

// ---------------------------------------------------------------------------
// Mappers depuis les types d'erreur Supabase
// ---------------------------------------------------------------------------

/** Codes Postgres reconnus comme erreurs d'autorisation (RLS deny). */
const RLS_DENIED_CODES = new Set(['42501', 'PGRST301', '403']);

/** Codes Postgres reconnus comme "not found". */
const NOT_FOUND_CODES = new Set(['PGRST116']);

/**
 * Convertit une erreur Supabase (PostgrestError | StorageError | AuthError)
 * en AppError discriminé.
 *
 * Invariant 4.A.15 : aucun champ PII n'est loggé depuis ce mapper.
 * Le `message` transféré vient directement de Supabase — attention côté appelant
 * à ne pas l'exposer brut à l'UI si le message contient des détails sensibles.
 */
export function appErrorFromSupabase(
  error: PostgrestError | StorageApiError | AuthError,
  operation = 'unknown',
): AppError {
  // AuthError (vient de @supabase/auth-js via @supabase/supabase-js)
  if ('status' in error && 'name' in error && isAuthError(error)) {
    const status = (error as AuthError).status;
    if (status === 401) return authError('expired');
    if (status === 403) return authError('unauthorized');
    return authError('no_jwt');
  }

  // PostgrestError (vient de @supabase/postgrest-js)
  if ('code' in error && 'details' in error && 'hint' in error) {
    const pgErr = error as PostgrestError;
    const code = pgErr.code ?? '';
    const message = pgErr.message ?? 'Erreur Supabase inconnue';

    if (RLS_DENIED_CODES.has(code)) {
      return authError('unauthorized');
    }

    if (NOT_FOUND_CODES.has(code)) {
      // PGRST116 = "The result contains 0 rows"
      return notFoundError(operation, '');
    }

    // Codes fiscaux reconnus
    if (code === '42501' || message.includes('hash_mismatch') || message.includes('idempotency')) {
      const fiscalCode: FiscalErrorCode = message.includes('hash_mismatch')
        ? 'hash_mismatch'
        : message.includes('idempotency')
          ? 'idempotency_violation'
          : '42501';
      return fiscalError(fiscalCode, message);
    }

    return supabaseError(operation, message, code || undefined);
  }

  // StorageApiError (vient de @supabase/storage-js)
  if ('message' in error) {
    return supabaseError(operation, (error as StorageApiError).message);
  }

  return unknownError('Erreur Supabase non reconnue');
}

/** Guard interne — différencie AuthError de PostgrestError/StorageApiError. */
function isAuthError(error: PostgrestError | StorageApiError | AuthError): error is AuthError {
  return 'name' in error && typeof (error as AuthError).name === 'string' && 'status' in error;
}

// ---------------------------------------------------------------------------
// Helpers de message lisible
// ---------------------------------------------------------------------------

/**
 * Extrait un message lisible depuis n'importe quel variant d'AppError.
 * Nécessaire car AppError est une union discriminée et seuls certains variants
 * ont un champ `message` directement.
 */
export function appErrorMessage(e: AppError): string {
  switch (e.type) {
    case 'network':
      return e.cause.message;
    case 'auth':
      return e.reason === 'no_jwt'
        ? 'Non authentifié'
        : e.reason === 'expired'
          ? 'Session expirée'
          : 'Accès non autorisé';
    case 'validation':
      return e.message;
    case 'not_found':
      return `${e.resource} introuvable`;
    case 'fiscal':
      return e.message;
    case 'supabase':
      return e.message;
    case 'unknown':
      return e.message;
  }
}

/**
 * Raccourci : extrait le message lisible d'un Result<T, AppError> en erreur.
 * ⚠ N'appeler QUE dans une branche où `result.ok === false` est garanti.
 */
export function errMsg<T>(result: Result<T, AppError>): string {
  return appErrorMessage(errOf(result));
}
